import React, { useState, useEffect } from 'react';
import { Shield, MessageCircle, Clock, Check, ChevronDown, ChevronUp, Lock, FileText, RefreshCw, Smartphone, Mail, Car, Home, Wrench, Wifi, Heart } from 'lucide-react';
import { Helmet } from "react-helmet";
import { supabase } from './supabase';
import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = 'G-17M25WY3CK';

const App = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    ReactGA.initialize(GA_MEASUREMENT_ID);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: supabaseError } = await supabase
      .from('waitlist')
      .insert([{ email }]);

    if (supabaseError) {
      if (supabaseError.code === '23505') {
        setSubmitted(true);
        ReactGA.event({ action: 'sign_up', category: 'engagement', label: 'waitlist_existing' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } else {
      setSubmitted(true);
      ReactGA.event({ action: 'sign_up', category: 'engagement', label: 'waitlist_new' });
    }
    setLoading(false);
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>HomeEaze - Your Home Admin on WhatsApp</title>
        <meta name="description" content="The AI assistant that tracks your insurance, finds better renewals, and saves you money — without you lifting a finger." />
        <script
          src="https://getlaunchlist.com/js/widget.js"
          type="text/javascript"
          defer
        />
      </Helmet>

      {/* Navigation */}
      <nav className="container mx-auto px-6 py-6 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <div className="bg-green-500 p-2 rounded-lg">
            <MessageCircle className="text-white w-6 h-6" />
          </div>
          <span className="text-2xl font-bold text-gray-900">HomeEaze</span>
        </div>
        <a href="#join" className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-full font-medium transition-colors">
          Get Early Access
        </a>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* WhatsApp-style bubbles background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-green-200 rounded-full opacity-30"></div>
          <div className="absolute top-40 -right-10 w-48 h-48 bg-green-100 rounded-full opacity-40"></div>
          <div className="absolute top-60 left-1/4 w-32 h-32 bg-green-300 rounded-full opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-green-200 rounded-full opacity-25"></div>
          <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-green-100 rounded-full opacity-30"></div>
        </div>
        <div className="container mx-auto px-6 py-16 md:py-24 text-center relative z-10">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            Now in Beta — Join the Waitlist
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Your Home Admin, Handled on WhatsApp
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8">
            The AI assistant that tracks your insurance, finds better renewals, and saves you money — without you lifting a finger.
          </p>
          <p className="text-lg text-gray-500 mb-10">
            Just forward your documents or give access to your email, and HomeEaze does the rest — automatically.
          </p>
          <a href="#join" className="inline-block bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-4 rounded-full font-semibold transition-colors shadow-lg hover:shadow-xl">
            Join the Waitlist — It's Free
          </a>
          <p className="mt-4 text-sm text-gray-500 flex items-center justify-center gap-2">
            <MessageCircle className="w-4 h-4" /> No app download required
          </p>
        </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Getting started takes less than a minute. No forms, no accounts, no hassle.
          </p>

          <div className="grid md:grid-cols-5 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Start a Chat', desc: 'Message HomeEaze on WhatsApp. No app to download, no account to create.' },
              { step: '02', title: 'Share Documents', desc: 'Forward insurance docs or grant email access. HomeEaze reads everything.' },
              { step: '03', title: 'Sit Back', desc: 'We extract details, track renewals, and monitor for better deals.' },
              { step: '04', title: 'Get Notified', desc: 'Before renewal, receive 3-5 fresh quotes right in your chat.' },
              { step: '05', title: 'One Reply', desc: 'Say "Yes" to the quote you want. We complete the renewal for you.' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Why You'll Love HomeEaze
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Clock, title: 'Save Hours of Admin', desc: 'No more comparing prices or filling out forms. HomeEaze does the legwork so you don\'t have to.' },
              { icon: RefreshCw, title: 'Never Miss a Renewal', desc: 'You\'ll always know exactly when something needs renewing — with plenty of time to switch if needed.' },
              { icon: Shield, title: 'Always Get the Best Price', desc: 'We fetch 3-5 quotes before every renewal, so you\'re never paying more than you should.' },
              { icon: Smartphone, title: 'No Apps or Forms', desc: 'Everything happens inside WhatsApp — the app you already use every day.' },
              { icon: Heart, title: 'Free Forever', desc: 'HomeEaze earns commission from insurers when you renew. You pay nothing. Ever.' },
              { icon: FileText, title: 'Automatic Tracking', desc: 'Forward documents or connect email. We handle the rest automatically.' }
            ].map((benefit, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <benefit.icon className="w-10 h-10 text-green-500 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why WhatsApp */}
      <section className="bg-green-50 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Why WhatsApp?
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: 'You Already Trust It', desc: 'Over 2 billion users. You already know how it works. No learning curve, no new passwords.' },
              { title: 'It\'s Always Ready', desc: 'Your phone is always nearby. Need to confirm a quote? Just reply. Done.' },
              { title: 'Conversations Feel Human', desc: 'HomeEaze feels like texting a helpful mate — not filling out a government form.' }
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="text-white w-8 h-8" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Privacy */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Lock className="w-12 h-12 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Your Data Is Safe & Secure
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-left mt-12">
              {[
                { title: 'End-to-End Encrypted', desc: 'All messages and documents are protected with WhatsApp\'s encryption.' },
                { title: 'You\'re in Control', desc: 'Grant or revoke access anytime. We only use data to find renewals.' },
                { title: 'We Never Sell Data', desc: 'Your information stays yours. No third parties, no advertisers.' }
              ].map((item, i) => (
                <div key={i}>
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm text-gray-500">
              HomeEaze works with regulated insurers and brokers. FCA-compliant.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            What Can HomeEaze Handle?
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Pretty much anything that needs renewing or tracking in your home.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Home, title: 'Home Insurance', desc: 'Track your policy, get renewal quotes, switch with one reply' },
              { icon: Car, title: 'Car Insurance', desc: 'Never overpay again; we find deals before expiry' },
              { icon: Wrench, title: 'Boiler & Warranties', desc: 'Stay covered without the paperwork' },
              { icon: RefreshCw, title: 'MOT Reminders', desc: 'Get notified when your MOT is due' },
              { icon: Wifi, title: 'Broadband Deals', desc: 'Switch to better deals automatically' },
              { icon: Heart, title: 'Pet Insurance', desc: 'Keep your furry friends covered' }
            ].map((item, i) => (
              <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-start gap-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <item.icon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>

          <div className="max-w-2xl mx-auto space-y-4">
            {[
              { q: 'How does HomeEaze work?', a: 'Just message us on WhatsApp and either forward your insurance documents or grant read-only access to your email. HomeEaze extracts the details, monitors for renewals, and fetches fresh quotes before your policy expires.' },
              { q: 'Is it really free?', a: 'Yes. HomeEaze is completely free for users. We earn a commission from insurers and brokers when you complete a renewal through us.' },
              { q: 'How do you make money?', a: 'When HomeEaze helps you switch or renew, the insurer or broker pays us a commission. This is standard industry practice and means you never pay a penny.' },
              { q: 'What documents can I upload?', a: 'You can forward any insurance document — home, car, boiler, appliance warranty, broadband contract, MOT certificates, and more. HomeEaze can also scan your email for existing policies.' },
              { q: 'Do I need to install anything?', a: 'No. HomeEaze lives entirely inside WhatsApp. No app download, no new account, no passwords to remember.' },
              { q: 'Is my data safe?', a: 'Absolutely. Your data is encrypted end-to-end via WhatsApp, and we only use it to find and process renewals. You can revoke access at any time, and we never sell your data to third parties.' }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-gray-600">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="join" className="bg-green-500 py-16 md:py-24">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stop Overpaying. Start WhatsApp-ing.
          </h2>
          <p className="text-green-100 text-lg mb-8 max-w-xl mx-auto">
            HomeEaze handles your home admin so you don't have to. Join the early access list and be the first to experience the future of hassle-free home management.
          </p>

          {submitted ? (
            <div className="bg-white/10 backdrop-blur-sm inline-block px-8 py-4 rounded-xl">
              <p className="text-white font-semibold text-lg">Thanks for joining! We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-5 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-700 text-white px-8 py-4 rounded-full font-semibold transition-colors"
                >
                  {loading ? 'Joining...' : 'Join the Waitlist'}
                </button>
              </div>
              {error && <p className="text-white text-sm mt-2">{error}</p>}
              <p className="text-green-100 text-sm mt-4">
                Join the Waitlist — It's Free
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="bg-green-500 p-1.5 rounded-lg">
              <MessageCircle className="text-white w-5 h-5" />
            </div>
            <span className="text-white font-bold">HomeEaze</span>
          </div>
          <p className="text-sm">© 2026 HomeEaze. Your home admin, sorted on WhatsApp.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
